import React from 'react';
import styled from 'styled-components';

import MetaTags from 'components/meta-tags';
import { GoogleLogin } from 'components/social-login/google-login';
import { AppleLogin } from 'components/social-login/apple-login';
import type { IrlEvent } from 'src/gql/graphql';
import { useAccountSignupAnalytics, useAccountSignupViewed } from 'src/hooks/use-account-signup-analytics';
import { Card } from './card';
import { IrlEventLogo } from './irl-event-logo';
import { ContentHeader, Heading, Description, FooterLink, Content } from './styles';
import { useIrlEventParams } from '../hooks/use-irl-event-params';

type IrlEventSignupStartProps = {
  irlEvent: IrlEvent;
  onContinueWithEmail: () => void;
};

export function IrlEventSignupStart({ irlEvent, onContinueWithEmail }: IrlEventSignupStartProps): JSX.Element {
  const { publicContextSessionId } = useIrlEventParams();
  // Both provider buttons are visible on this step; the canonical hook emits once per method with
  // flag/visibility/no-burn semantics built in.
  useAccountSignupViewed({ surface: 'irlEvent', method: 'google' });
  useAccountSignupViewed({ surface: 'irlEvent', method: 'apple' });
  const googleAnalytics = useAccountSignupAnalytics({ surface: 'irlEvent', method: 'google' });
  const appleAnalytics = useAccountSignupAnalytics({ surface: 'irlEvent', method: 'apple' });

  // Build per-provider redirect URLs: authSource marks the social return for post-auth handling,
  // authMethod lets the returning page attribute a backend-confirmed new account to its provider.
  const buildRedirectUrl = (method: 'apple' | 'google'): string => {
    if (typeof window === 'undefined') {
      return '';
    }
    const url = new URL(window.location.href);
    url.searchParams.set('authSource', 'social');
    url.searchParams.set('authMethod', method);
    return url.toString();
  };

  const { title, description, headerImageUrl } = irlEvent;

  return (
    <>
      <MetaTags title={title} description={description} />

      <Card data-testid='irl-event-signup-start'>
        <IrlEventLogo headerImageUrl={headerImageUrl ?? null} />

        <ContentHeader>
          <Heading data-testid='irl-event-signup-start-title'>{title}</Heading>
          <Description data-testid='irl-event-signup-start-description'>{description}</Description>
        </ContentHeader>

        <Content>
          <SocialLogins>
            <GoogleLogin
              redirectUrl={buildRedirectUrl('google')}
              onSubmit={googleAnalytics.trackSubmitted}
              publicContextSessionId={publicContextSessionId}
            />
            <AppleLogin
              redirectUrl={buildRedirectUrl('apple')}
              onSubmit={appleAnalytics.trackSubmitted}
              publicContextSessionId={publicContextSessionId}
            />
          </SocialLogins>

          <FooterLink onClick={onContinueWithEmail} data-testid='irl-event-continue-with-email'>
            Continue with email
          </FooterLink>
        </Content>
      </Card>
    </>
  );
}

const SocialLogins = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin: 0 auto 25px;
`;
